import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import SiteIcons from "@/components/SiteIcons/SiteIcons";
import { useSidebarContext } from "@/contexts/SidebarContext";
import { useUpdateDataContext } from "@/contexts/UpdateDataContext";

import "@/components/membership/Template/TemplateEditorStyles.css";

function TemplateSidebar({ template }) {
  const { setCrossProductList1, setCrossProductList2, path, updatePath } =
    useSidebarContext();
  const [openStates, setOpenStates] = useState({});
  const { workflow } = useUpdateDataContext();

  useEffect(() => {
    // reset sidebar path on component mount
    if (workflow === "political") {
      updatePath(0, Object.keys(template)[0]); // the first key of a political workflow template might be anything
      updatePath(1, "volunteer");
    } else {
      updatePath(0, "Member");
      updatePath(1, "member"); // we need to hard code this so "job" doesn't show up as the first category.
    }
    resetCrossProductPathElements();
    // updatePath and resetCrossProductPathElements come from the sidebar
    // context and are recreated each render; intentionally fire only on
    // mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOuterClick = (category) => {
    setOpenStates((prevState) => ({
      ...prevState,
      [category]: !prevState[category],
    }));
    updatePath(0, category);
    updatePath(1, Object.keys(template[category])[0]);
    // if not cross product, reset cross product path elements
    if (!hasCrossProduct(category, Object.keys(template[category])[0])) {
      resetCrossProductPathElements();
    }
  };

  const handleInnerClick = (category, subCategory) => {
    updatePath(0, category);
    updatePath(1, subCategory);
    // check for crossproduct
    if (hasCrossProduct(category, subCategory)) {
      const adaFields = template[category][subCategory];
      setCrossProductList1(adaFields[0]["formula_data"][0]);
      updatePath(2, adaFields[0]["formula_data"][0][0]);
      // handle nested cross product
      if (adaFields[0]["formula_data"][1]) {
        setCrossProductList2(adaFields[0]["formula_data"][1]);
        updatePath(3, adaFields[0]["formula_data"][1][0]);
      } else {
        updatePath(3, null);
        setCrossProductList2(null);
      }
    } else {
      resetCrossProductPathElements();
    }
  };

  const hasCrossProduct = (category, subCategory) => {
    return template[category][subCategory][0]["formula"] === "crossproduct";
  };

  const resetCrossProductPathElements = () => {
    updatePath(2, null);
    setCrossProductList1(null);
    setCrossProductList2(null);
  };

  return workflow === "political" ? null : (
    <>
      <List
        sx={{ width: "35%", bgcolor: "background.paper" }}
        component="nav"
        data-testid="template-sidebar"
      >
        {Object.keys(template).map((cat) => (
          <Box key={cat}>
            <ListItemButton onClick={() => handleOuterClick(cat)}>
              {openStates[cat] ? (
                <SiteIcons iconstring="remove" color="blue" />
              ) : (
                <SiteIcons iconstring="add" color="blue" />
              )}
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    className="template-editor-mapperItem-item-text"
                  >
                    {cat}
                  </Typography>
                }
              />
            </ListItemButton>
            <Collapse in={openStates[cat]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {Object.keys(template[cat])
                  .filter((subCat) => !subCat.includes(".")) // do not show "dotted" crossproduct keys in sidebar
                  .map((subCat) => (
                    <ListItemButton
                      className={
                        subCat === path[1] ? "selected-sidebar-category" : ""
                      }
                      selected={subCat === path[1]}
                      sx={{ pl: 4 }}
                      onClick={() => handleInnerClick(cat, subCat)}
                      key={subCat}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body1"
                            className="template-editor-mapperItem-nestedItemText"
                          >
                            {subCat}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
              </List>
            </Collapse>
          </Box>
        ))}
      </List>
    </>
  );
}

TemplateSidebar.propTypes = {
  template: PropTypes.object,
};

export default TemplateSidebar;
